import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ShieldCheck, Plus, Trash2, ArrowRight, FileText, ArrowRightLeft } from "lucide-react";
import { AMP_PLAN_STATUS_LABELS, type AmpPlanStatus } from "@/lib/amp/constants";
import type { AmpPlan } from "@/hooks/useAmpPlans";

interface Props {
  plans: AmpPlan[];
  progress: Record<string, { done: number; total: number }>;
  name: string;
  onNameChange: (value: string) => void;
  busy: boolean;
  onCreate: () => void;
  onOpen: (planId: string) => void;
  onDelete: (planId: string) => void;
}

export const AmpMainDocumentPanel: React.FC<Props> = ({
  plans,
  progress,
  name,
  onNameChange,
  busy,
  onCreate,
  onOpen,
  onDelete,
}) => {
  const hasPlans = plans.length > 0;

  return (
    <Card className="overflow-hidden border-2 border-primary/30 shadow-lg animate-fade-in">
      <div className="hero-dark-gradient text-on-hero px-6 py-6">
        <div className="flex flex-wrap items-start gap-4">
          <div className="p-3 rounded-xl bg-white/10 border border-white/20">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1 sm:min-w-[16rem]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-white/20 border border-white/30">
                Huvuddokument
              </span>
            </div>
            <h2 className="text-2xl font-bold mt-2">Arbetsmiljöplan</h2>
            <p className="text-sm on-hero-dim mt-1 max-w-2xl">
              Projektets huvuddokument enligt AFS 2023:3 kap. 4. Övriga dokument nedan är bilagor
              som arbetsmiljöplanen hänvisar till.
            </p>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Input
              className="h-9 w-44 bg-background/90"
              placeholder="Namn (valfritt)"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
            />
            <Button size="sm" variant="secondary" onClick={onCreate} disabled={busy}>
              <Plus className="h-4 w-4 mr-1" />
              {hasPlans ? "Ny AMP" : "Upprätta AMP"}
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 bg-card">
        {!hasPlans ? (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-dashed p-5">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Ingen arbetsmiljöplan är upprättad ännu</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Upprätta AMP:n först – den förifylls automatiskt från bilagorna i Bas-processen.
                </p>
              </div>
            </div>
            <Button onClick={onCreate} disabled={busy}>
              Upprätta arbetsmiljöplan
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {plans.map((plan) => {
              const p = progress[plan.id];
              const pct = p && p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
              return (
                <div
                  key={plan.id}
                  className="group flex flex-wrap items-center gap-4 rounded-lg border bg-background p-4 transition-shadow hover:shadow-md cursor-pointer"
                  onClick={() => onOpen(plan.id)}
                >
                  <div className="p-2 rounded-lg bg-primary/10">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                  </div>

                  <div className="min-w-0 flex-1 sm:min-w-[12rem]">
                    <h3 className="font-semibold">{plan.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Version {plan.version} ·{" "}
                      {AMP_PLAN_STATUS_LABELS[plan.status as AmpPlanStatus] || plan.status} ·
                      uppdaterad {new Date(plan.updated_at).toLocaleDateString("sv-SE")}
                    </p>
                    <p className="text-xs mt-1">
                      {plan.handover_at ? (
                        <span className="inline-flex items-center gap-1 text-primary">
                          <ArrowRightLeft className="h-3 w-3" />
                          Överlämnad till Bas-U{" "}
                          {new Date(plan.handover_at).toLocaleDateString("sv-SE")}
                          {plan.handover_to ? ` · ${plan.handover_to}` : ""}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <ArrowRightLeft className="h-3 w-3" />
                          Bas-P upprättar – ej överlämnad till Bas-U
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="w-full sm:w-48">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>Ifyllda avsnitt</span>
                      <span>
                        {p ? `${p.done}/${p.total}` : "–"}
                      </span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>

                  <div className="flex items-center gap-2 ml-auto">
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); onOpen(plan.id); }}>
                      Öppna arbetsmiljöplanen
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(plan.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default AmpMainDocumentPanel;
